import { useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "mt-1.5 block w-full min-h-[48px] rounded-lg border border-sage/25 bg-white px-4 py-3 text-base text-ink placeholder:text-muted-foreground focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage-light/40";

export const Field = ({ label, id, required, children, hint }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-ink">
      {label} {required && <span aria-hidden="true" className="text-sage">*</span>}
      {required && <span className="sr-only">(required)</span>}
    </label>
    {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    {children}
  </div>
);

export const TextInput = ({ id, type = "text", required, placeholder, testId, hint }) => (
  <input
    id={id}
    name={id}
    type={type}
    required={required}
    placeholder={placeholder}
    aria-describedby={hint ? `${id}-hint` : undefined}
    className={inputClass}
    data-testid={testId}
  />
);

export const TextArea = ({ id, rows = 4, placeholder, testId, hint }) => (
  <textarea
    id={id}
    name={id}
    rows={rows}
    placeholder={placeholder}
    aria-describedby={hint ? `${id}-hint` : undefined}
    className={`${inputClass} min-h-0`}
    data-testid={testId}
  />
);

// A real, backend-connected form. Shows success ONLY when the server
// confirms the submission was stored; otherwise shows the honest error.
export const LiveForm = ({
  type,
  children,
  submitLabel = "Submit",
  testId,
  successMessage = "Thank you — your message has been received.",
}) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    const data = {};
    for (const key of new Set([...fd.keys()])) {
      const all = fd.getAll(key);
      data[key] = all.length > 1 ? all.join(", ") : String(all[0]);
    }
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/submissions/${type}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === "string" ? err.detail : "Something went wrong. Please try again.";
        throw new Error(detail);
      }
      form.reset();
      setStatus({ kind: "success", msg: successMessage });
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Could not send. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} data-testid={testId}>
      {children}
      <button
        type="submit"
        disabled={busy}
        className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full bg-sage px-8 py-3 text-sm font-semibold text-cream transition-colors hover:bg-sage-light disabled:opacity-60"
        data-testid={`${testId}-submit-button`}
      >
        {busy ? "Sending…" : submitLabel}
      </button>
      {status && (
        <p
          role="status"
          className={`mt-4 rounded-lg p-3 text-sm font-medium ${
            status.kind === "success"
              ? "bg-gold-soft/60 text-ink"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
          data-testid={`${testId}-status`}
        >
          {status.msg}
        </p>
      )}
    </form>
  );
};
